/**
 * Signup Protection — disposable email blocking + client-side rate limiting
 */

// Common disposable email domains (subset — server-side validation should also exist)
const DISPOSABLE_DOMAINS = new Set([
  "mailinator.com", "guerrillamail.com", "guerrillamail.net", "tempmail.com",
  "throwaway.email", "temp-mail.org", "fakeinbox.com", "sharklasers.com",
  "guerrillamailblock.com", "grr.la", "dispostable.com", "yopmail.com",
  "trashmail.com", "trashmail.net", "trashmail.org", "10minutemail.com",
  "minutemail.com", "tempail.com", "tempr.email", "discard.email",
  "discardmail.com", "mailnesia.com", "maildrop.cc", "mailsac.com",
  "mohmal.com", "getnada.com", "emailondeck.com", "33mail.com",
  "guerrillamail.info", "guerrillamail.biz", "guerrillamail.de",
  "tempinbox.com", "burnermail.io", "inboxkitten.com",
  "jetable.org", "trash-mail.com", "mytemp.email", "tempmailaddress.com",
  "tmpmail.org", "tmpmail.net", "mailcatch.com", "spamgourmet.com",
  "harakirimail.com", "tmail.ws", "mailexpire.com", "incognitomail.org",
  "anonbox.net", "bugmenot.com", "deadaddress.com", "fakemailgenerator.com",
  "filzmail.com", "getairmail.com", "mailforspam.com", "mailinater.com",
  "safetymail.info", "spamfree24.org", "trashymail.com", "wegwerfmail.de",
]);

/**
 * Check if an email uses a known disposable domain
 */
export function isDisposableEmail(email: string): boolean {
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) return false;
  return DISPOSABLE_DOMAINS.has(domain);
}

/**
 * Client-side signup rate limiter — prevents rapid signups from same browser
 */
const SIGNUP_RATE_KEY = "vzl_signup_attempts";
const SIGNUP_WINDOW_MS = 15 * 60 * 1000; // 15 minutes
const MAX_SIGNUP_ATTEMPTS = 3;

interface SignupAttempt {
  timestamps: number[];
}

function getSignupAttempts(): SignupAttempt {
  try {
    const raw = localStorage.getItem(SIGNUP_RATE_KEY);
    if (!raw) return { timestamps: [] };
    return JSON.parse(raw);
  } catch {
    return { timestamps: [] };
  }
}

function saveSignupAttempts(attempts: SignupAttempt) {
  try {
    localStorage.setItem(SIGNUP_RATE_KEY, JSON.stringify(attempts));
  } catch {}
}

/**
 * Check if signup is rate-limited. Returns remaining seconds if limited, or 0 if allowed.
 */
export function checkSignupRateLimit(): { allowed: boolean; retryAfterSeconds: number } {
  const now = Date.now();
  const attempts = getSignupAttempts();
  
  // Filter to only recent attempts
  const recentTimestamps = attempts.timestamps.filter(t => now - t < SIGNUP_WINDOW_MS);
  
  if (recentTimestamps.length >= MAX_SIGNUP_ATTEMPTS) {
    const oldestRecent = Math.min(...recentTimestamps);
    const retryAfter = Math.ceil((oldestRecent + SIGNUP_WINDOW_MS - now) / 1000);
    return { allowed: false, retryAfterSeconds: Math.max(retryAfter, 1) };
  }
  
  return { allowed: true, retryAfterSeconds: 0 };
}

/**
 * Record a signup attempt
 */
export function recordSignupAttempt() {
  const now = Date.now();
  const attempts = getSignupAttempts();
  const recentTimestamps = attempts.timestamps.filter(t => now - t < SIGNUP_WINDOW_MS);
  recentTimestamps.push(now);
  saveSignupAttempts({ timestamps: recentTimestamps });
}

/**
 * Validate email format strictly
 */
export function isValidEmailFormat(email: string): boolean {
  // RFC 5322 simplified
  const re = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  if (!re.test(email)) return false;
  // Must have at least one dot in domain
  const domain = email.split("@")[1];
  return domain?.includes(".") ?? false;
}

/**
 * Password strength validation
 * Requires: min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
 */
export interface PasswordStrengthResult {
  valid: boolean;
  errors: string[];
  score: number; // 0-5
}

export function validatePasswordStrength(password: string): PasswordStrengthResult {
  const errors: string[] = [];
  let score = 0;

  if (password.length >= 8) score++; else errors.push("At least 8 characters");
  if (/[A-Z]/.test(password)) score++; else errors.push("One uppercase letter");
  if (/[a-z]/.test(password)) score++; else errors.push("One lowercase letter");
  if (/[0-9]/.test(password)) score++; else errors.push("One number");
  if (/[^A-Za-z0-9]/.test(password)) score++; else errors.push("One special character (!@#$%...)");

  return { valid: errors.length === 0, errors, score };
}

/**
 * Client-side sign-in rate limiter — prevents brute-force login attempts
 */
const LOGIN_RATE_KEY = "vzl_login_attempts";
const LOGIN_WINDOW_MS = 15 * 60 * 1000; // 15 minutes
const MAX_LOGIN_ATTEMPTS = 5;

function getLoginAttempts(): { timestamps: number[] } {
  try {
    const raw = localStorage.getItem(LOGIN_RATE_KEY);
    if (!raw) return { timestamps: [] };
    return JSON.parse(raw);
  } catch {
    return { timestamps: [] };
  }
}

function saveLoginAttempts(attempts: { timestamps: number[] }) {
  try {
    localStorage.setItem(LOGIN_RATE_KEY, JSON.stringify(attempts));
  } catch {}
}

export function checkLoginRateLimit(): { allowed: boolean; retryAfterSeconds: number } {
  const now = Date.now();
  const attempts = getLoginAttempts();
  const recentTimestamps = attempts.timestamps.filter(t => now - t < LOGIN_WINDOW_MS);

  if (recentTimestamps.length >= MAX_LOGIN_ATTEMPTS) {
    const oldestRecent = Math.min(...recentTimestamps);
    const retryAfter = Math.ceil((oldestRecent + LOGIN_WINDOW_MS - now) / 1000);
    return { allowed: false, retryAfterSeconds: Math.max(retryAfter, 1) };
  }

  return { allowed: true, retryAfterSeconds: 0 };
}

export function recordLoginAttempt() {
  const now = Date.now();
  const attempts = getLoginAttempts();
  const recentTimestamps = attempts.timestamps.filter(t => now - t < LOGIN_WINDOW_MS);
  recentTimestamps.push(now);
  saveLoginAttempts({ timestamps: recentTimestamps });
}

export function clearLoginAttempts() {
  try { localStorage.removeItem(LOGIN_RATE_KEY); } catch {}
}
